import { Grid2 as Grid } from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { useCollections } from "api/collections";
import { getBackendErrors, getErrorMessage } from "api/errors";
import { FormButtons } from "components/FormControls/FormButtons";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { TextInput } from "components/FormControls/TextInput";
import { OLModal } from "components/Tooltips/OLModal";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { Form, Formik } from "formik";
import { useMessage } from "hooks/intl";
import { useParams } from "react-router-dom";
import { backendErrors } from "utilities/backendErrors";
import { Modals } from "../SingleCollection";

const useStyles = makeStyles(() =>
  createStyles({
    modalButtons: {
      justifyContent: "center",
      marginBottom: 0,
      "& button": {
        flex: 1,
      },
    },
  }),
);

interface Props {
  isOpen: boolean;
  setSelectedModal: ({
    mode,
    isOpen,
  }: {
    mode: Modals | null;
    isOpen: boolean;
  }) => void;
}

const AddValueModal = ({ isOpen, setSelectedModal }: Props) => {
  const classes = useStyles();
  const { message } = useMessage();
  const { notify } = useNotifications();
  const { id: singleCollectionId } = useParams<{ id: string }>();
  const { addCollectionValue } = useCollections();

  return (
    <OLModal
      open={isOpen}
      header={message({ id: "addSingleItem" })}
      onClose={() => setSelectedModal({ mode: null, isOpen: false })}
      modalWidth={545}
      data-test-id="single-collection-add-value-modal"
    >
      <Formik
        initialValues={{ value: "", description: "" }}
        onSubmit={async (values, { setSubmitting, setStatus }) => {
          setSubmitting(true);
          setStatus({});

          try {
            await addCollectionValue({
              ...values,
              groupOfValuesId: singleCollectionId as string,
            });

            notify(
              message({
                id: "itemAdded",
              }),
            );

            setSelectedModal({ mode: null, isOpen: false });
          } catch (error) {
            setSubmitting(false);
            notify(getErrorMessage(error));
            const errors = backendErrors(getBackendErrors(error));
            setStatus(errors);
          }
        }}
      >
        {({ handleSubmit, isSubmitting }) => (
          <Form onSubmit={handleSubmit}>
            <Grid container spacing={12}>
              <Grid size={12}>
                <TextInput
                  label={<RequiredFieldLabel text={message({ id: "value" })} />}
                  name="value"
                  data-test-id="value"
                  width="100%"
                />
              </Grid>
              <Grid size={12}>
                <TextInput
                  label={message({ id: "description" })}
                  name="description"
                  data-test-id="description"
                  width="100%"
                />
              </Grid>
              <Grid size={12}>
                <FormButtons
                  className={classes.modalButtons}
                  disableSubmit={isSubmitting}
                  onCancelClick={() =>
                    setSelectedModal({ mode: null, isOpen: false })
                  }
                  onSubmitClick={() => handleSubmit()}
                />
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </OLModal>
  );
};

export default AddValueModal;
